"use client";

import { Grid05 } from "@layer/icons";
import { cn } from "@layer/utils/cn";

import type { AppRouteType } from "@layer/utils/lib/routing";
import Link from "next/link";
import * as React from "react";
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "../../../components/sidebar";

type Props = {
	routes: AppRouteType[];
	pathname: string;
	headerComponent?: React.ReactNode;
	isMinimalShell?: boolean;
	alwaysCollapsed?: boolean;
};

const isActive = (pathname: string, href: string) => {
	if (href === "/") return pathname === "/";
	return pathname === href || pathname.startsWith(href + "/");
};

const renderRouteIcon = (icon: AppRouteType["icon"]) => {
	if (React.isValidElement(icon)) return icon;

	if (
		typeof icon === "function" ||
		(typeof icon === "object" && icon !== null)
	) {
		const IconComp = icon as React.ElementType<{ className?: string }>;
		return <IconComp />;
	}

	return <Grid05 className="size-4" />;
};

export const AppSidebar = ({
	routes,
	pathname,
	headerComponent,
	isMinimalShell,
	alwaysCollapsed,
}: Props) => {
	const visibleRoutes = React.useMemo(
		() => routes.filter((r) => !r.hidden),
		[routes],
	);

	return (
		<Sidebar
			variant="floating"
			collapsible="icon"
			isMinimalShell={isMinimalShell}
		>
			<SidebarHeader className="gap-2">{headerComponent}</SidebarHeader>
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{visibleRoutes.map((route) => {
								const active = isActive(pathname, route.href);

								return (
									<SidebarMenuItem key={route.href}>
										<SidebarMenuButton
											asChild
											isActive={active}
											tooltip={route.name}
										>
											<Link
												href={route.href}
												className={cn(
													"flex items-center gap-3",
												)}
											>
												{renderRouteIcon(route.icon)}
												<span>{route.name}</span>
											</Link>
										</SidebarMenuButton>
									</SidebarMenuItem>
								);
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
		</Sidebar>
	);
};
